import { checkSectionCss } from '../policy/index.js';
import { checkModuleOutput } from '../policy/html.js';
import type { FreeformSectionT, JsonValueT } from '../schema/types.js';
import type { ContentSnapshotT, DefModuleMountT, ModuleDataValidator } from '../schema/split.js';
import { commerceCatalog } from './commerce-catalog/index.js';
import { contentEvents } from './events/index.js';
import { formsContact } from './forms-contact/index.js';
import { ModuleOpError, resolveModuleData, resolveMount, type FirstPartyModule, type ResolveIssue } from './kernel.js';
import { venueMenuHours } from './venue-menu-hours/index.js';

/** Deep-freeze a module descriptor so the "frozen registry" is real at
 *  runtime, not merely a ReadonlyMap type. */
function deepFreezeModule(module: FirstPartyModule): FirstPartyModule {
  Object.freeze(module.operations);
  Object.freeze(module.views);
  for (const operation of Object.values(module.operations)) Object.freeze(operation);
  for (const view of Object.values(module.views)) Object.freeze(view);
  return Object.freeze(module);
}

const MODULES: readonly FirstPartyModule[] = [venueMenuHours, formsContact, commerceCatalog, contentEvents].map(deepFreezeModule);

/**
 * Read-only registry facade. The backing Map is private; the returned object
 * exposes only `get`/`has` (no set/delete/clear), so — unlike Object.freeze
 * on a Map, which does nothing — this genuinely cannot be mutated at runtime.
 */
function buildReadonlyRegistry(modules: readonly FirstPartyModule[]): ReadonlyMap<string, FirstPartyModule> {
  const map = new Map<string, FirstPartyModule>(modules.map((module) => [module.id, module]));
  const facade: ReadonlyMap<string, FirstPartyModule> = {
    get: (key) => map.get(key),
    has: (key) => map.has(key),
    get size() {
      return map.size;
    },
    keys: () => map.keys(),
    values: () => map.values(),
    entries: () => map.entries(),
    // Native Map.forEach passes the BACKING map as the 3rd arg, which would
    // leak a mutable reference — hand callers the facade instead.
    forEach: (callback, thisArg) => {
      for (const [key, value] of map) callback.call(thisArg, value, key, facade);
    },
    [Symbol.iterator]: () => map[Symbol.iterator](),
  };
  return Object.freeze(facade);
}

export function buildRegistry(): ReadonlyMap<string, FirstPartyModule> {
  return buildReadonlyRegistry(MODULES);
}

/** The single frozen first-party registry, built once at module load. */
export const defaultRegistry = buildRegistry();

function isResolveIssue(result: { html: string; css?: string } | ResolveIssue): result is ResolveIssue {
  return 'code' in result;
}

/**
 * Render a module mount to a freeform section. A resolve failure throws
 * loudly (never renders empty). Used by resolveToContent's module callback.
 */
export function resolveModuleSection(
  registry: ReadonlyMap<string, FirstPartyModule>,
  mount: DefModuleMountT,
  moduleData: ContentSnapshotT['modules'][string] | undefined,
): FreeformSectionT {
  const resolved = resolveMount(registry, mount, moduleData);
  if (isResolveIssue(resolved)) {
    throw new ModuleOpError(resolved.code, `${resolved.where}: ${resolved.message}`);
  }
  // Defense in depth: a module view's output goes into a raw freeform sink,
  // so validate it against the template policy even though modules are
  // first-party — a compromised/buggy registry can't emit active content.
  const outputIssues = checkModuleOutput(resolved.html);
  if (outputIssues.length > 0) {
    throw new ModuleOpError('module-output-unsafe', `${mount.id}: ${outputIssues[0]!.detail}`);
  }
  if (resolved.css !== undefined) {
    const cssIssues = checkSectionCss(resolved.css);
    if (cssIssues.length > 0) throw new ModuleOpError('module-css-unsafe', `${mount.id}: ${cssIssues[0]!.detail}`);
  }
  const section: FreeformSectionT = {
    kind: 'freeform',
    id: mount.id,
    anchor: mount.anchor,
    html: resolved.html,
    editableManifest: [],
  };
  if (resolved.css !== undefined) section.css = resolved.css;
  return section;
}

/**
 * A ModuleDataValidator (for validateSnapshotAgainstDefinition) backed by a
 * registry: validates a mount's module data/config without rendering.
 */
export function makeModuleDataValidator(registry: ReadonlyMap<string, FirstPartyModule>): ModuleDataValidator {
  return (mount, stored) => {
    const resolved = resolveModuleData(registry, mount, stored as { dataVersion: number; data: JsonValueT } | undefined);
    return 'code' in resolved ? `${resolved.code}: ${resolved.message}` : null;
  };
}
